// src/features/dashboardSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../axiosInstance';
import { classifyMeasureCheckpointStatus } from '../utils/checkpointStatus';

const buildMeasureStatusPercentages = (counts) => {
    const totalStatuses =
        counts.noStatusCount +
        counts.stoppedCount +
        counts.notStartedCount +
        counts.delayedProgressCount +
        counts.onTrackProgressCount +
        counts.completedCount;

    let noStatus = 0;
    let stopped = 0;
    let notStarted = 0;
    let delayedProgress = 0;
    let onTrackProgress = 0;
    let completed = 0;

    if (totalStatuses > 0) {
        noStatus = Math.floor((counts.noStatusCount / totalStatuses) * 100);
        stopped = Math.floor((counts.stoppedCount / totalStatuses) * 100);
        notStarted = Math.floor((counts.notStartedCount / totalStatuses) * 100);
        delayedProgress = Math.floor((counts.delayedProgressCount / totalStatuses) * 100);
        onTrackProgress = Math.floor((counts.onTrackProgressCount / totalStatuses) * 100);
        completed = Math.floor((counts.completedCount / totalStatuses) * 100);

        const sum = noStatus + stopped + notStarted + delayedProgress + onTrackProgress + completed;
        if (sum < 100) {
            const maxValue = Math.max(
                noStatus,
                stopped,
                notStarted,
                delayedProgress,
                onTrackProgress,
                completed
            );
            const remainder = 100 - sum;

            if (maxValue === noStatus) noStatus += remainder;
            else if (maxValue === stopped) stopped += remainder;
            else if (maxValue === notStarted) notStarted += remainder;
            else if (maxValue === delayedProgress) delayedProgress += remainder;
            else if (maxValue === onTrackProgress) onTrackProgress += remainder;
            else completed += remainder;
        }
    }

    return {
        noStatus,
        stopped,
        notStarted,
        delayedProgress,
        onTrackProgress,
        completed,
    };
};

// Utility function to clean internal count fields from the mapped data
const cleanMappedData = (data) => {
    return data.map(unit => {
        const {
            _noStatusCount,
            _stoppedCount,
            _notStartedCount,
            _delayedProgressCount,
            _onTrackProgressCount,
            _completedCount,
            _actionBoards,
            _tags,
            children,
            ...cleanUnit
        } = unit;
        return {
            ...cleanUnit,
            children: children ? cleanMappedData(children) : [],
            tags: _tags || [],
        };
    });
};

const createMeasureStatusCounts = () => ({
    noStatusCount: 0,
    stoppedCount: 0,
    notStartedCount: 0,
    delayedProgressCount: 0,
    onTrackProgressCount: 0,
    completedCount: 0,
});

const applyMeasureStatusCount = (counts, status, statusCount = 1) => {
    switch (classifyMeasureCheckpointStatus(status)) {
        case 'noStatus':
            counts.noStatusCount += statusCount;
            break;
        case 'stopped':
            counts.stoppedCount += statusCount;
            break;
        case 'notStarted':
            counts.notStartedCount += statusCount;
            break;
        case 'delayedProgress':
            counts.delayedProgressCount += statusCount;
            break;
        case 'onTrackProgress':
            counts.onTrackProgressCount += statusCount;
            break;
        case 'completed':
            counts.completedCount += statusCount;
            break;
        default:
            counts.noStatusCount += statusCount;
            break;
    }
};

const buildDashboardUnit = ({
    unit,
    tags,
    actionBoards,
    actionAreas,
    finishedActionAreas,
    measures,
    finishedMeasures,
    children,
    counts,
}) => {
    const percentages = buildMeasureStatusPercentages(counts);

    return {
        id: unit.unit_id || unit.unitname,
        unitname: unit.unitname,
        actionBoards,
        actionAreas,
        finishedActionAreas,
        visibility_level: unit.visibility_level,
        tags,
        measures,
        finishedMeasures,
        noStatus: percentages.noStatus,
        stopped: percentages.stopped,
        notStarted: percentages.notStarted,
        delayedProgress: percentages.delayedProgress,
        onTrackProgress: percentages.onTrackProgress,
        completed: percentages.completed,
        children,
        _noStatusCount: counts.noStatusCount,
        _stoppedCount: counts.stoppedCount,
        _notStartedCount: counts.notStartedCount,
        _delayedProgressCount: counts.delayedProgressCount,
        _onTrackProgressCount: counts.onTrackProgressCount,
        _completedCount: counts.completedCount,
        _actionBoards: actionBoards,
        _tags: tags,
        green: percentages.onTrackProgress,
        yellow: percentages.delayedProgress,
        red: percentages.stopped,
        grey: percentages.notStarted,
    };
};

// Async thunk to fetch dashboard data from the new API
export const fetchDashboardData = createAsyncThunk(
    'dashboard/fetchDashboardData',
    async (_, { rejectWithValue }) => {
        try {
            const [checkpointsResponse, dashboardResponse, defaultQuestionsResponse] = await Promise.all([
                axiosInstance.get('/api/checkpoints/'),
                axiosInstance.get('/api/reports/dashboard/'),
                axiosInstance.get('/api/default-questions/')
            ]);

            let checkpoints = checkpointsResponse.data;

            if (!Array.isArray(checkpoints)) {
                if (checkpoints.results && Array.isArray(checkpoints.results)) {
                    checkpoints = checkpoints.results;
                } else {
                    checkpoints = [checkpoints];
                }
            }

            const formattedCheckpoints = checkpoints.map(checkpoint => {
                const actionAreaStatuses = checkpoint.checkpoint_type === 'actionareas'
                    ? (checkpoint.actionarea_statuses || checkpoint.statuses || [])
                    : [];

                return {
                    id: checkpoint.id,
                    checkpoint_name: checkpoint.checkpoint_name,
                    end_date: checkpoint.ends_at,
                    checkpoint_type: checkpoint.checkpoint_type,
                    statuses: checkpoint.statuses || [],
                    measure_statuses: checkpoint.measure_statuses || [],
                    actionarea_statuses: actionAreaStatuses
                };
            });

            formattedCheckpoints.sort((a, b) => {
                const dateA = a.end_date ? new Date(a.end_date) : new Date(0);
                const dateB = b.end_date ? new Date(b.end_date) : new Date(0);
                return dateA - dateB;
            });

            const data = dashboardResponse.data;
            const rootUnits = data;

            const mapUnitData = (unit) => {
                const thisUnitCountsAsActionBoard = (unit.visibility_level === 1 || unit.visibility_level === 2) ? 1 : 0;

                if (unit.children && unit.children.length > 0) {
                    const childrenData = unit.children.map(mapUnitData);

                    const aggregated = childrenData.reduce(
                        (acc, child) => {
                            acc.noStatusCount += child._noStatusCount;
                            acc.stoppedCount += child._stoppedCount;
                            acc.notStartedCount += child._notStartedCount;
                            acc.delayedProgressCount += child._delayedProgressCount;
                            acc.onTrackProgressCount += child._onTrackProgressCount;
                            acc.completedCount += child._completedCount;
                            acc.actionAreas += child.actionAreas;
                            acc.finishedActionAreas += child.finishedActionAreas;
                            acc.measures += child.measures;
                            acc.finishedMeasures += child.finishedMeasures;
                            acc.actionBoards += child._actionBoards;

                            child.tags.forEach(tag => {
                                if (tag.tag_name) {
                                    const key = tag.tag_name;
                                    if (acc.tagsMap.has(key)) {
                                        const existing = acc.tagsMap.get(key);
                                        acc.tagsMap.set(key, {
                                            ...existing,
                                            count: existing.count + (tag.count || 1)
                                        });
                                    } else {
                                        acc.tagsMap.set(key, { ...tag });
                                    }
                                }
                            });

                            return acc;
                        },
                        {
                            ...createMeasureStatusCounts(),
                            actionAreas: 0,
                            finishedActionAreas: 0,
                            measures: 0,
                            finishedMeasures: 0,
                            actionBoards: 0,
                            tagsMap: new Map()
                        }
                    );

                    if (unit.tags?.length > 0) {
                        unit.tags
                            .filter(tag => tag.tag_name !== null)
                            .forEach(tag => {
                                const key = tag.tag_name;
                                if (aggregated.tagsMap.has(key)) {
                                    const existing = aggregated.tagsMap.get(key);
                                    aggregated.tagsMap.set(key, {
                                        ...existing,
                                        count: existing.count + (tag.count || 1)
                                    });
                                } else {
                                    aggregated.tagsMap.set(key, {
                                        tag_name: tag.tag_name,
                                        count: tag.count || 1
                                    });
                                }
                            });
                    }

                    const aggregatedTags = Array.from(aggregated.tagsMap.values());

                    return buildDashboardUnit({
                        unit,
                        tags: aggregatedTags,
                        actionBoards: thisUnitCountsAsActionBoard + aggregated.actionBoards,
                        actionAreas: (unit.count_of_actionareas || 0) + aggregated.actionAreas,
                        finishedActionAreas: (unit.count_of_completed_actionareas || 0) + aggregated.finishedActionAreas,
                        measures: (unit.count_of_measures || 0) + aggregated.measures,
                        finishedMeasures: (unit.count_of_completed_measures || 0) + aggregated.finishedMeasures,
                        children: childrenData,
                        counts: aggregated,
                    });
                }

                const counts = createMeasureStatusCounts();

                if (unit.checkpoint_status && unit.checkpoint_status.length > 0) {
                    unit.checkpoint_status.forEach(statusObj => {
                        if (statusObj.checkpoint_type !== 'measures') {
                            return;
                        }

                        applyMeasureStatusCount(counts, statusObj.status_name, statusObj.status_count);
                    });
                }

                if (
                    counts.noStatusCount === 0 &&
                    counts.stoppedCount === 0 &&
                    counts.notStartedCount === 0 &&
                    counts.delayedProgressCount === 0 &&
                    counts.onTrackProgressCount === 0 &&
                    counts.completedCount === 0
                ) {
                    counts.noStatusCount = unit.count_of_measures || 0;
                }

                const tags = unit.tags?.length > 0
                    ? unit.tags
                        .filter(tag => tag.tag_name !== null)
                        .map(tag => ({
                            id: tag.id || null,
                            tag_name: tag.tag_name || 'Unkategorisiert',
                            count: tag.count || 1
                        }))
                    : [];

                return buildDashboardUnit({
                    unit,
                    tags,
                    actionBoards: thisUnitCountsAsActionBoard,
                    actionAreas: unit.count_of_actionareas,
                    finishedActionAreas: unit.count_of_completed_actionareas,
                    measures: unit.count_of_measures,
                    finishedMeasures: unit.count_of_completed_measures,
                    children: [],
                    counts,
                });
            };

            const mappedDataWithCounts = rootUnits.map(mapUnitData);
            const mappedData = cleanMappedData(mappedDataWithCounts);

            return {
                data: mappedData,
                checkpoints: formattedCheckpoints,
                defaultQuestions: defaultQuestionsResponse.data
            };
        } catch (error) {
            const errorMessage =
                error.response?.data?.detail ||
                error.message ||
                'An unknown error occurred while fetching dashboard data.';
            return rejectWithValue(errorMessage);
        }
    },
    {
        condition: (_, { getState }) => {
            const { dashboard } = getState();
            if (dashboard.status === 'loading' || dashboard.status === 'succeeded') {
                return false;
            }
            return true;
        },
    }
);

const initialState = {
    data: [],
    checkpoints: [],
    defaultQuestions: [],
    status: 'idle',
    error: null,
};

const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchDashboardData.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchDashboardData.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.data = action.payload.data;
                state.checkpoints = action.payload.checkpoints;
                state.defaultQuestions = action.payload.defaultQuestions;
            })
            .addCase(fetchDashboardData.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
                state.data = [];
                state.checkpoints = [];
            });
    },
});

export default dashboardSlice.reducer;
